import React from 'react';
import { labelDocumentType, labelPictureType, sortByOrder, type OmdDocument, type OmdProduct } from '../../../utils/omdProductView';
import type { OmdPanelSettings } from '../../../utils/omdPanelSettings';
import { OmdDocumentCards, OmdPictureGrid } from '../shared/OmdDocumentCards';
import { OmdRawJsonSection } from '../shared/OmdRawJsonSection';
import { OmdSection } from '../shared/OmdSection';

type OmdDocumentsTabProps = {
  product: OmdProduct;
  rawProduct: unknown;
  settings: OmdPanelSettings;
  onOpenLightbox: (index: number) => void;
  onOpenDocument: (doc: OmdDocument) => void;
  hideRawJson?: boolean;
};

export function OmdDocumentsTab({
  product,
  rawProduct,
  settings,
  onOpenLightbox,
  onOpenDocument,
  hideRawJson,
}: OmdDocumentsTabProps) {
  const documents = sortByOrder((product.documents ?? []).filter((d) => d?.url));
  const pictures = sortByOrder((product.pictures ?? []).filter((p) => p?.url || p?.urlThumbnail));

  if (documents.length === 0 && pictures.length === 0) {
    return <p className="text-sm text-app-text-subtle">Keine Dokumente oder Bilder verfügbar.</p>;
  }

  return (
    <div className="space-y-4">
      {documents.length > 0 && (
        <OmdSection title="Dokumente" settings={settings} hidden={false}>
          <OmdDocumentCards product={product} settings={settings} onOpenDocument={onOpenDocument} />
        </OmdSection>
      )}

      {pictures.length > 0 && (
        <OmdSection title="Bilder" settings={settings}>
          <OmdPictureGrid product={product} onOpenLightbox={onOpenLightbox} />
        </OmdSection>
      )}

      {!hideRawJson && <OmdRawJsonSection product={rawProduct} />}
    </div>
  );
}

export function buildLightboxPictures(product: OmdProduct) {
  return sortByOrder((product.pictures ?? []).filter((p) => p?.url)).map((pic) => ({
    url: pic.url!,
    urlThumbnail: pic.urlThumbnail,
    title: pic.description || pic.filename || labelPictureType(pic.type),
  }));
}

export function docPreviewFromDocument(doc: OmdDocument) {
  return {
    url: doc.url!,
    title: doc.description || doc.filename || labelDocumentType(doc.type),
    filename: doc.filename,
    documentType: doc.type,
  };
}
